import type { Action } from '@atlassian/react-sweet-state';

export type PureStateTransformation<State> = (state: Readonly<State>) => Readonly<Partial<State>>;

export type PureActionCreator<State, FNArgs> = (
	// @ts-expect-error - TS2370 - A rest parameter must be of an array type.
	...parameters: FNArgs
) => PureStateTransformation<State>;

export type ConvertedActionCreator<State, FNArgs> = {
	// @ts-expect-error - TS2370 - A rest parameter must be of an array type.
	(...parameters: FNArgs): Action<State>;
	pure: PureActionCreator<State, FNArgs>;
};

/**
 * Turns a pure state transformation creator into a react-sweet-state Action creator.
 *
 * For example:
 *     type State = {| counter: number |};
 *
 *     const increment = pureAction<State, [number]>(inc => state => ({
 *         counter: state.counter + inc,
 *     });
 *
 * The function returned by `pureAction` has the correct type:
 *
 *     (inc: number) => Action<State>
 *
 * And exposes the pure transformation creator in its `.pure` property for testing:
 *
 *     const initialState = { counter: 0 };
 *     const incrementedState = increment.pure(10)(initialState);
 *
 */
const pureAction = <State, FNArgs extends ReadonlyArray<unknown>>(
	actionCreator: PureActionCreator<State, FNArgs>,
): ConvertedActionCreator<State, FNArgs> => {
	const convertedActionCreator: ConvertedActionCreator<State, FNArgs> = (...parameters: FNArgs) => {
		const action: PureStateTransformation<State> = actionCreator(...parameters);
		return ({ getState, setState }) => {
			const state: Readonly<State> = getState();
			const newPartialState: Readonly<Partial<State>> = action(state);
			return setState(newPartialState);
		};
	};
	convertedActionCreator.pure = actionCreator;
	return convertedActionCreator;
};

export default pureAction;
